import { useState, useEffect } from 'react'
import { Shield, Trash2, Download, Clock, AlertTriangle, CheckCircle, LogOut } from 'lucide-react'
import { supabase } from '../lib/supabase'
import { trelloService } from '../lib/trello'

interface DataStats {
  totalTasks: number;
  completedTasks: number;
  pendingTasks: number;
  cardHistory: number;
  meetingNotes: number;
  oldestTask: string | null;
  oldestHistory: string | null;
}

export default function DataPrivacySettings() {
  const [dataStats, setDataStats] = useState<DataStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [trelloConnected, setTrelloConnected] = useState(false);
  const [disconnectLoading, setDisconnectLoading] = useState(false);

  useEffect(() => {
    loadDataStats();
    checkTrelloConnection();
  }, []);

  const loadDataStats = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      // Get task count
      const { count: taskCount } = await supabase
        .from('tasks')
        .select('*', { count: 'exact', head: true })
        .eq('user_id', user.id);

      // Get card history count
      const { count: cardCount } = await supabase
        .from('card_history')
        .select('*', { count: 'exact', head: true })
        .eq('user_id', user.id);

      // Get meeting notes count
      const { count: notesCount } = await supabase
        .from('meeting_notes')
        .select('*', { count: 'exact', head: true })
        .eq('user_id', user.id);

      setDataStats({
        totalTasks: taskCount || 0,
        completedTasks: 0, // Placeholder, needs actual data
        pendingTasks: 0, // Placeholder, needs actual data
        cardHistory: cardCount || 0,
        meetingNotes: notesCount || 0,
        oldestTask: null, // Placeholder, needs actual data
        oldestHistory: null, // Placeholder, needs actual data
      });
    } catch (error) {
      console.error('Error loading data stats:', error);
    } finally {
      setLoading(false);
    }
  };

  const checkTrelloConnection = async () => {
    try {
      await trelloService.init();
      setTrelloConnected(trelloService.isConnected());
    } catch (error) {
      console.error('Error checking Trello connection:', error);
      setTrelloConnected(false);
    }
  };

  const handleDisconnectTrello = async () => {
    setDisconnectLoading(true);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');

      // Clear Trello token from database
      await supabase
        .from('user_trello_tokens')
        .delete()
        .eq('user_id', user.id);

      // Clear local member cache
      trelloService.clearAllUserData();

      setTrelloConnected(false);
      alert('Trello account disconnected successfully');
    } catch (error) {
      console.error('Error disconnecting Trello:', error);
      alert('Failed to disconnect Trello account');
    } finally {
      setDisconnectLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="max-w-4xl mx-auto p-6">
        <div className="animate-pulse">
          <div className="h-8 bg-gray-200 rounded w-1/3 mb-6"></div>
          <div className="space-y-4">
            <div className="h-32 bg-gray-200 rounded"></div>
            <div className="h-32 bg-gray-200 rounded"></div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto p-6">
      <h1 className="text-3xl font-bold text-gray-900 mb-8">Settings</h1>

      {/* Data Overview */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
        <h2 className="text-xl font-semibold text-gray-900 mb-4">Data Overview</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="text-center p-4 bg-blue-50 rounded-lg">
            <div className="text-2xl font-bold text-blue-600">{dataStats?.totalTasks || 0}</div>
            <div className="text-sm text-blue-700">Tasks</div>
          </div>
          <div className="text-center p-4 bg-green-50 rounded-lg">
            <div className="text-2xl font-bold text-green-600">{dataStats?.cardHistory || 0}</div>
            <div className="text-sm text-green-700">Cards Created</div>
          </div>
          <div className="text-center p-4 bg-purple-50 rounded-lg">
            <div className="text-2xl font-bold text-purple-600">{dataStats?.meetingNotes || 0}</div>
            <div className="text-sm text-purple-700">Meeting Notes</div>
          </div>
        </div>
      </div>

      {/* Account Management */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
        <h2 className="text-xl font-semibold text-gray-900 mb-4">Account Management</h2>
        <div className="space-y-4">
          <div className="flex items-center justify-between p-4 border border-gray-200 rounded-lg">
            <div className="flex items-center space-x-3">
              <div className={`w-3 h-3 rounded-full ${trelloConnected ? 'bg-green-500' : 'bg-gray-300'}`}></div>
              <div>
                <div className="font-medium text-gray-900">Trello Connection</div>
                <div className="text-sm text-gray-500">
                  {trelloConnected ? 'Connected' : 'Not connected'}
                </div>
              </div>
            </div>
            {trelloConnected && (
              <button
                onClick={handleDisconnectTrello}
                disabled={disconnectLoading}
                className="flex items-center space-x-2 px-4 py-2 text-sm font-medium text-red-600 bg-red-50 border border-red-200 rounded-md hover:bg-red-100 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <LogOut className="w-4 h-4" />
                {disconnectLoading ? 'Disconnecting...' : 'Disconnect'}
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Privacy Information */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <h2 className="text-xl font-semibold text-gray-900 mb-4 flex items-center">
          <Shield className="w-5 h-5 mr-2 text-gray-600" />
          Privacy Information
        </h2>
        <div className="space-y-3 text-gray-700">
          <div className="flex items-start space-x-3">
            <CheckCircle className="w-5 h-5 text-green-500 mt-0.5 flex-shrink-0" />
            <span>Tasks are automatically cleaned up after being converted to Trello cards (24 hours)</span>
          </div>
          <div className="flex items-start space-x-3">
            <CheckCircle className="w-5 h-5 text-green-500 mt-0.5 flex-shrink-0" />
            <span>Unused tasks (not converted to cards) are cleaned up immediately</span>
          </div>
          <div className="flex items-start space-x-3">
            <CheckCircle className="w-5 h-5 text-green-500 mt-0.5 flex-shrink-0" />
            <span>We store your Trello access token securely in our database for app functionality</span>
          </div>
          <div className="flex items-start space-x-3">
            <CheckCircle className="w-5 h-5 text-green-500 mt-0.5 flex-shrink-0" />
            <span>Card creation history is kept for your reference and analytics</span>
          </div>
          <div className="flex items-start space-x-3">
            <CheckCircle className="w-5 h-5 text-green-500 mt-0.5 flex-shrink-0" />
            <span>All data is encrypted in transit and at rest</span>
          </div>
          <div className="flex items-start space-x-3">
            <CheckCircle className="w-5 h-5 text-green-500 mt-0.5 flex-shrink-0" />
            <span>You can disconnect your Trello account anytime to remove your token</span>
          </div>
        </div>
      </div>

      {/* Hidden for MVP - Uncomment when needed */}
      {false && (
        <>
          {/* Automatic Data Cleanup */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
            <h2 className="text-xl font-semibold text-gray-900 mb-4 flex items-center">
              <Clock className="w-5 h-5 mr-2 text-gray-600" />
              Automatic Data Cleanup
            </h2>
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <div>
                  <div className="font-medium text-gray-900">Enable Auto-Cleanup</div>
                  <div className="text-sm text-gray-500">
                    Automatically remove tasks after they've been converted to Trello cards
                  </div>
                </div>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input type="checkbox" className="sr-only peer" defaultChecked />
                  <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                </label>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Retention Period (hours after card creation)
                </label>
                <select className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500">
                  <option>24 hours (recommended)</option>
                  <option>48 hours</option>
                  <option>72 hours</option>
                  <option>1 week</option>
                </select>
                <p className="text-sm text-gray-500 mt-1">
                  Tasks are automatically deleted this many hours after being converted to Trello cards
                </p>
              </div>
            </div>
          </div>

          {/* Data Management Actions */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">Data Management Actions</h2>
            <div className="space-y-3">
              <button className="w-full flex items-center justify-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700">
                <Trash2 className="w-4 h-4" />
                <span>Cleanup Now</span>
              </button>
              <button className="w-full flex items-center justify-center space-x-2 px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700">
                <Download className="w-4 h-4" />
                <span>Export Data</span>
              </button>
              <button className="w-full flex items-center justify-center space-x-2 px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700">
                <AlertTriangle className="w-4 h-4" />
                <span>Delete All Data</span>
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
} 